
export const DESIGN_TOKENS = {
  // Ultra-Compact Spacing System (80% scaled for maximum density)
  SPACING: {
    XXS: '2px',   // 0.5 * base - micro spacing (unchanged for precision)
    XS: '3px',    // 4px * 0.8 = 3.2 ≈ 3px - minimal spacing
    SM: '6px',    // 8px * 0.8 = 6.4 ≈ 6px - small spacing
    MD: '10px',   // 12px * 0.8 = 9.6 ≈ 10px - medium spacing
    LG: '13px',   // 16px * 0.8 = 12.8 ≈ 13px - large spacing
    XL: '16px',   // 20px * 0.8 = 16px - extra large spacing
    XXL: '19px',  // 24px * 0.8 = 19.2 ≈ 19px - double extra large
    XXXL: '26px', // 32px * 0.8 = 25.6 ≈ 26px - maximum spacing
  },

  // Enhanced Typography System with better hierarchy
  TYPOGRAPHY: {
    FONT_FAMILY: {
      PRIMARY: "'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif",
      MONO: "'JetBrains Mono', 'Fira Code', 'Monaco', 'Consolas', monospace",
      DISPLAY: "'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif"
    },
    FONT_SIZE: {
      XS: '10px',   // Micro text
      SM: '12px',   // Small text
      BASE: '14px', // Body text
      MD: '15px',   // Medium text
      LG: '16px',   // Large text
      XL: '18px',   // Extra large
      XXL: '20px',  // Heading small
      XXXL: '24px', // Heading medium
      DISPLAY_SM: '28px', // Display small
      DISPLAY_MD: '32px', // Display medium
      DISPLAY_LG: '36px'  // Display large
    },
    FONT_WEIGHT: {
      LIGHT: 300,
      REGULAR: 400,
      MEDIUM: 500,
      SEMIBOLD: 600,
      BOLD: 700,
      EXTRABOLD: 800
    },
    LINE_HEIGHT: {
      TIGHT: 1.25,  // For headings
      NORMAL: 1.5,  // For body text
      RELAXED: 1.75 // For large text blocks
    },
    LETTER_SPACING: {
      TIGHT: '-0.025em',
      NORMAL: '0',
      WIDE: '0.025em',
      WIDER: '0.05em'
    }
  },

  // Color System
  COLORS: {
    // Primary Brand Colors
    PRIMARY: {
      50: '#e6f7ff',
      100: '#bae7ff',
      200: '#91d5ff',
      300: '#69c0ff',
      400: '#40a9ff',
      500: '#1890ff', // Main primary
      600: '#096dd9',
      700: '#0050b3',
      800: '#003a8c',
      900: '#002766'
    },

    // Semantic Colors
    SUCCESS: {
      50: '#f6ffed',
      500: '#52c41a',
      600: '#389e0d'
    },
    WARNING: {
      50: '#fffbe6',
      500: '#faad14',
      600: '#d48806'
    },
    ERROR: {
      50: '#fff2f0',
      500: '#ff4d4f',
      600: '#cf1322'
    },
    INFO: {
      50: '#e6f7ff',
      500: '#1890ff',
      600: '#096dd9'
    },

    // Neutral Colors
    GRAY: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#f0f0f0',
      300: '#d9d9d9',
      400: '#bfbfbf',
      500: '#8c8c8c',
      600: '#595959',
      700: '#434343',
      800: '#262626',
      900: '#1f1f1f'
    },

    // Background Colors
    BACKGROUND: {
      BODY: '#fafafb',
      CARD: '#ffffff',
      HOVER: '#f5f5f5',
      ACTIVE: '#e6f7ff',
      DARK_BODY: '#1b2531', // from @body-background in dark.less
      DARK_CARD: '#283142', // from @component-background in dark.less
    },
    // Text Colors
    TEXT: {
      PRIMARY: '#1a1a1a', // default text (light mode)
      SECONDARY: '#595959', // default secondary (light mode)
      DARK_PRIMARY: '#b4bed2', // from @text-color in dark.less
      DARK_SECONDARY: '#72849a', // from @gray-light in dark.less
    },
  },

  // Border System
  BORDERS: {
    WIDTH: {
      NONE: '0',
      THIN: '1px',
      MEDIUM: '2px',
      THICK: '4px'
    },
    RADIUS: {
      NONE: '0',
      SM: '4px',
      BASE: '8px',
      LG: '12px',
      XL: '16px',
      FULL: '9999px'
    },
    STYLE: {
      SOLID: 'solid',
      DASHED: 'dashed',
      DOTTED: 'dotted'
    }
  },

  // Enhanced Shadow System with layered, realistic shadows
  SHADOWS: {
    NONE: 'none',
    // Subtle elevation
    XS: '0 1px 2px 0 rgba(0, 0, 0, 0.03), 0 1px 3px 0 rgba(0, 0, 0, 0.02)',
    // Small elevation - buttons, cards
    SM: '0 1px 2px 0 rgba(0, 0, 0, 0.05), 0 2px 4px 0 rgba(0, 0, 0, 0.04), 0 1px 6px 0 rgba(0, 0, 0, 0.03)',
    // Medium elevation - dropdowns, tooltips
    BASE: '0 2px 4px 0 rgba(0, 0, 0, 0.06), 0 4px 8px 0 rgba(0, 0, 0, 0.05), 0 2px 12px 0 rgba(0, 0, 0, 0.04)',
    // Large elevation - modals, popovers
    MD: '0 4px 8px 0 rgba(0, 0, 0, 0.08), 0 8px 16px 0 rgba(0, 0, 0, 0.06), 0 4px 24px 0 rgba(0, 0, 0, 0.05)',
    // Extra large elevation - drawers, overlays
    LG: '0 8px 16px 0 rgba(0, 0, 0, 0.1), 0 16px 32px 0 rgba(0, 0, 0, 0.08), 0 8px 48px 0 rgba(0, 0, 0, 0.06)',
    // Maximum elevation - full screen overlays
    XL: '0 16px 32px 0 rgba(0, 0, 0, 0.12), 0 32px 64px 0 rgba(0, 0, 0, 0.1), 0 16px 96px 0 rgba(0, 0, 0, 0.08)',
    // Inset shadows for inputs
    INNER: 'inset 0 1px 2px 0 rgba(0, 0, 0, 0.06), inset 0 1px 4px 0 rgba(0, 0, 0, 0.04)',
    // Colored shadows for interactive elements
    PRIMARY: '0 2px 8px 0 rgba(24, 144, 255, 0.15), 0 4px 16px 0 rgba(24, 144, 255, 0.1)',
    SUCCESS: '0 2px 8px 0 rgba(82, 196, 26, 0.15), 0 4px 16px 0 rgba(82, 196, 26, 0.1)',
    WARNING: '0 2px 8px 0 rgba(250, 173, 20, 0.15), 0 4px 16px 0 rgba(250, 173, 20, 0.1)',
    ERROR: '0 2px 8px 0 rgba(255, 77, 79, 0.15), 0 4px 16px 0 rgba(255, 77, 79, 0.1)'
  },

  // Z-Index System
  Z_INDEX: {
    DROPDOWN: 1000,
    STICKY: 1020,
    FIXED: 1030,
    MODAL_BACKDROP: 1040,
    MODAL: 1050,
    POPOVER: 1060,
    TOOLTIP: 1070
  },

  // Animation System
  ANIMATION: {
    DURATION: {
      FAST: '150ms',
      BASE: '300ms',
      SLOW: '500ms'
    },
    EASING: {
      LINEAR: 'linear',
      EASE: 'ease',
      EASE_IN: 'ease-in',
      EASE_OUT: 'ease-out',
      EASE_IN_OUT: 'ease-in-out'
    }
  },

  // Layout System
  LAYOUT: {
    CONTAINER: {
      SM: '640px',
      MD: '768px',
      LG: '1024px',
      XL: '1280px',
      XXL: '1400px'
    },
    HEADER_HEIGHT: '70px',
    SIDEBAR_WIDTH: '280px',
    SIDEBAR_COLLAPSED: '80px',
    FOOTER_HEIGHT: '60px'
  }
};

// Enhanced Component-specific design tokens for compact, elegant design
export const COMPONENT_TOKENS = {
  BUTTON: {
    HEIGHT: {
      XS: '19px',   // 24px * 0.8 = 19.2 ≈ 19px - Extra small buttons
      SM: '22px',   // 28px * 0.8 = 22.4 ≈ 22px - Small buttons
      BASE: '26px', // 32px * 0.8 = 25.6 ≈ 26px - Default buttons
      LG: '29px',   // 36px * 0.8 = 28.8 ≈ 29px - Large buttons
      XL: '32px'    // 40px * 0.8 = 32px - Extra large buttons
    },
    PADDING: {
      XS: `${DESIGN_TOKENS.SPACING.XS} ${DESIGN_TOKENS.SPACING.SM}`,
      SM: `${DESIGN_TOKENS.SPACING.XS} ${DESIGN_TOKENS.SPACING.MD}`,
      BASE: `${DESIGN_TOKENS.SPACING.SM} ${DESIGN_TOKENS.SPACING.LG}`,
      LG: `${DESIGN_TOKENS.SPACING.SM} ${DESIGN_TOKENS.SPACING.XL}`,
      XL: `${DESIGN_TOKENS.SPACING.MD} ${DESIGN_TOKENS.SPACING.XXL}`
    },
    BORDER_RADIUS: DESIGN_TOKENS.BORDERS.RADIUS.SM,
    FONT_WEIGHT: DESIGN_TOKENS.TYPOGRAPHY.FONT_WEIGHT.MEDIUM
  },

  CARD: {
    PADDING: {
      SM: DESIGN_TOKENS.SPACING.MD,
      BASE: DESIGN_TOKENS.SPACING.LG,
      LG: DESIGN_TOKENS.SPACING.XL
    },
    BORDER_RADIUS: DESIGN_TOKENS.BORDERS.RADIUS.BASE,
    SHADOW: DESIGN_TOKENS.SHADOWS.SM,
    BORDER: `1px solid ${DESIGN_TOKENS.COLORS.GRAY[200]}`,
    BACKGROUND: DESIGN_TOKENS.COLORS.BACKGROUND.CARD
  },

  INPUT: {
    HEIGHT: {
      SM: '26px',   // 32px * 0.8 = 25.6 ≈ 26px - Compact for better density
      BASE: '34px', // 42px * 0.8 = 33.6 ≈ 34px - Improved height scaled
      LG: '37px',   // 46px * 0.8 = 36.8 ≈ 37px - Proportionally scaled
      XL: '40px'    // 50px * 0.8 = 40px - Proportionally scaled
    },
    BORDER_RADIUS: DESIGN_TOKENS.BORDERS.RADIUS.SM,
    PADDING: `10px 13px`, // 12px * 0.8 ≈ 10px, 16px * 0.8 ≈ 13px - Scaled padding
    BORDER: `1px solid ${DESIGN_TOKENS.COLORS.GRAY[300]}`,
    BORDER_FOCUS: `1px solid ${DESIGN_TOKENS.COLORS.PRIMARY[500]}`,
    SHADOW_FOCUS: DESIGN_TOKENS.SHADOWS.PRIMARY
  },

  TABLE: {
    HEADER_HEIGHT: '32px',  // 40px * 0.8 = 32px - More compact
    ROW_HEIGHT: '29px',     // 36px * 0.8 = 28.8 ≈ 29px - More compact
    CELL_PADDING: `${DESIGN_TOKENS.SPACING.XS} ${DESIGN_TOKENS.SPACING.MD}`,
    BORDER: `1px solid ${DESIGN_TOKENS.COLORS.GRAY[200]}`,
    HEADER_BACKGROUND: DESIGN_TOKENS.COLORS.GRAY[50],
    ROW_HOVER: DESIGN_TOKENS.COLORS.BACKGROUND.HOVER
  },

  MODAL: {
    BORDER_RADIUS: DESIGN_TOKENS.BORDERS.RADIUS.LG,
    SHADOW: DESIGN_TOKENS.SHADOWS.LG,
    BACKDROP: 'rgba(0, 0, 0, 0.45)',
    PADDING: DESIGN_TOKENS.SPACING.XXL
  },

  FORM: {
    LABEL_MARGIN: DESIGN_TOKENS.SPACING.XS,
    FIELD_MARGIN: DESIGN_TOKENS.SPACING.MD,
    GROUP_MARGIN: DESIGN_TOKENS.SPACING.LG
  }
};

// Utility functions for consistent styling
export const getSpacing = (size) => DESIGN_TOKENS.SPACING[size.toUpperCase()] || size;
export const getColor = (color, shade = 500) => {
  const colorObj = DESIGN_TOKENS.COLORS[color.toUpperCase()];
  return typeof colorObj === 'object' ? colorObj[shade] : colorObj;
};
export const getShadow = (size) => DESIGN_TOKENS.SHADOWS[size.toUpperCase()] || size;
export const getBorderRadius = (size) => DESIGN_TOKENS.BORDERS.RADIUS[size.toUpperCase()] || size;

export default DESIGN_TOKENS;
